import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useAnimationControls } from "motion/react";
import { SiteHeader } from "./SiteHeader";
import { MysticalBg, MP } from "./MysticalBg";
import { BotLogo } from "./BotLogo";
import { DT, DL } from "./DeltaTypography";
const logoImg = "/assets/new-logo.png";
import { BackButton } from "./BackButton";

// ── Ticker de alertas activas ──────────────────────────────────────────────────
const TICKER_ALERTS = [
  "⬡ ALERTA ACTIVA — Nuevo caso de sextorsión registrado, zona norte CDMX",
  "⬡ SISTEMA OMNISCIENT OBSERVER — OPERATIVO EN CURSO",
  "⬡ ACCESO CIFRADO ESTABLECIDO — Conexión segura verificada",
  "⬡ ADVERTENCIA — Esta zona está bajo supervisión directa del CEO",
  "⬡ PROTOCOLO DELTA ACTIVADO — Nivel de amenaza: CRÍTICO",
  "⬡ CANAL ENCRIPTADO ACTIVO — Zero indexing confirmado",
  "⬡ CASO #DP-2026-0441 — Montadeudas neutralizado. Evidencia entregada a FGR",
  "⬡ DAÑO COLATERAL — Riesgo confirmado en despliegues sin autorización",
  "⬡ OMNISCIENT OBSERVER — No es un juego. Es un arma.",
  "⬡ BÚNKER EN LÍNEA — Zona Segura activa 24/7",
];

// ── Mensajes de aliento ────────────────────────────────────────────────────────
const CALMING_MESSAGES = [
  { greek: "Ήρθες στο σωστό μέρος.",                          spanish: "Llegaste al lugar correcto."                   },
  { greek: "Δεν είσαι μόνος/η εδώ.",                          spanish: "No estás solo/a en esto."                      },
  { greek: "Ανάπνευσε. Έχουμε τον έλεγχο.",                   spanish: "Respira. Nosotros tomamos el control."         },
  { greek: "Μην πληρώσεις τίποτα ακόμα.",                     spanish: "No pagues nada todavía."                      },
  { greek: "Η λύση υπάρχει. Εδώ είναι.",                      spanish: "La solución existe. Está aquí."               },
  { greek: "Το Delta Protec είναι στο πλευρό σου.",            spanish: "Delta Protec está a tu lado."                },
  { greek: "Ο φόβος είναι προσωρινός. Η δικαιοσύνη μόνιμη.",  spanish: "El miedo es temporal. La justicia permanente."},
  { greek: "Ηρεμία. Εμείς χτυπάμε πρώτοι.",                   spanish: "Calma. Nosotros golpeamos primero."           },
];

// ── Protocolos ─────────────────────────────────────────────────────────────────
const PROTOCOLS = [
  { id: 1, icon: "🚫", code: "PROTO-01", title: "NO PAGUES", subtitle: "Κανένα πληρωμή",
    body: "Ni un centavo. El pago no detiene al extorsionador — lo alimenta. Cada peso enviado es una señal de que el método funciona y abre la puerta a más exigencias.",
    color: MP.crimsonBright, border: "rgba(168,16,48,0.45)", glow: "rgba(168,16,48,0.15)" },
  { id: 2, icon: "📸", code: "PROTO-02", title: "CAPTURA TODO", subtitle: "Αποδεικτικά στοιχεία",
    body: "Screenshots con fecha y hora visible de cada mensaje, llamada, número y amenaza. La evidencia es tu arma legal más poderosa.",
    color: MP.goldBright, border: "rgba(255,160,40,0.45)", glow: "rgba(255,160,40,0.12)" },
  { id: 3, icon: "🔓", code: "PROTO-03", title: "NO BLOQUEES AÚN", subtitle: "Μην αποκλείσεις",
    body: "Mantén el canal abierto. Necesitamos ese hilo activo para rastrear el origen exacto del agresor. Bloquearlo borra el rastro.",
    color: MP.goldPale, border: "rgba(200,160,64,0.45)", glow: "rgba(200,160,64,0.1)" },
  { id: 4, icon: "📲", code: "PROTO-04", title: "CONTÁCTANOS YA", subtitle: "Επικοινωνήστε άμεσα",
    body: "Escríbenos de inmediato por Telegram o WhatsApp. Cada minuto que pasa le da ventaja al agresor. La respuesta del Búnker es inmediata.",
    color: MP.crimsonBright, border: "rgba(168,16,48,0.45)", glow: "rgba(168,16,48,0.15)" },
  { id: 5, icon: "🛡️", code: "PROTO-05", title: "ACTIVA PRIVACIDAD", subtitle: "Ασφάλεια τώρα",
    body: "Pon todas tus redes sociales en modo privado ahora mismo. Restringe quién ve tus fotos, videos y listas de contactos. Cierra las puertas abiertas.",
    color: MP.goldBright, border: "rgba(255,160,40,0.45)", glow: "rgba(255,160,40,0.12)" },
  { id: 6, icon: "🤫", code: "PROTO-06", title: "NO DIFUNDAS", subtitle: "Σιωπή τώρα",
    body: "No compartas el contenido amenazante con nadie más. Compartirlo puede agravar tu situación legal y darle al extorsionador más poder sobre ti.",
    color: MP.goldPale, border: "rgba(200,160,64,0.45)", glow: "rgba(200,160,64,0.1)" },
  { id: 7, icon: "👥", code: "PROTO-07", title: "ALERTA A LOS TUYOS", subtitle: "Προστασία οικογένειας",
    body: "Avisa brevemente a tu familia y amigos cercanos sin entrar en pánico. El extorsionador puede intentar contactarlos. Que estén alertas, no asustados.",
    color: MP.crimsonBright, border: "rgba(168,16,48,0.45)", glow: "rgba(168,16,48,0.15)" },
  { id: 8, icon: "📋", code: "PROTO-08", title: "DOCUMENTA TODO", subtitle: "Τεκμηρίωση πλήρης",
    body: "Anota fechas, horas exactas, números de teléfono, cuentas bancarias mencionadas y cualquier dato del agresor. Este expediente es la munición de Delta.",
    color: MP.goldBright, border: "rgba(255,160,40,0.45)", glow: "rgba(255,160,40,0.12)" },
];

// ── Tipos de extorsión ─────────────────────────────────────────────────────────
const EXTORTION_TYPES = [
  { id:1, icon:"📱", tag:"TIPO-A", title:"SEXTORSIÓN", greek:"Σεξουαλικός εκβιασμός",
    desc:"Te amenaza con publicar fotos o videos íntimos reales o manipulados. Generalmente contactan por redes sociales fingiendo interés romántico.",
    signals:["Pidió fotos íntimas antes","Dice tener 'material comprometedor'","Exige dinero o más material"],
    color: MP.crimsonBright, accent:"rgba(168,16,48,0.3)" },
  { id:2, icon:"💸", tag:"TIPO-B", title:"MONTADEUDAS", greek:"Παράνομη είσπραξη χρεών",
    desc:"Apps de préstamo rápido con contratos trampa. Acceden a tu agenda, fotos y datos para amenazar a tus contactos si no pagas intereses ilegales.",
    signals:["Accedió a tus contactos","Llaman a tu familia","Publican fotos tuyas en grupos"],
    color:"#e07b20", accent:"rgba(200,100,20,0.25)" },
  { id:3, icon:"🪪", tag:"TIPO-C", title:"ROBO DE IDENTIDAD", greek:"Κλοπή ταυτότητας",
    desc:"Usan tus datos personales para abrir cuentas, pedir créditos o cometer delitos en tu nombre. Puedes enterarte años después.",
    signals:["Créditos que no solicitaste","Cuentas bancarias desconocidas","Llamadas de cobro de deudas ajenas"],
    color: MP.goldBright, accent:"rgba(255,160,40,0.25)" },
  { id:4, icon:"☎️", tag:"TIPO-D", title:"EXTORSIÓN TELEFÓNICA", greek:"Τηλεφωνική εκβίαση",
    desc:"Llaman fingiendo ser autoridades, familia secuestrada o ganadores de premios. Generan urgencia y pánico para que actúes sin pensar.",
    signals:["Voz grabada o sintética","Exigen transferencia inmediata","No dejan colgar o verificar"],
    color: MP.goldPale, accent:"rgba(200,160,64,0.2)" },
  { id:5, icon:"💻", tag:"TIPO-E", title:"HACKEO & RANSOMWARE", greek:"Κυβερνοεπίθεση",
    desc:"Secuestran tu dispositivo, cuentas o archivos y piden rescate. Pueden bloquear tu negocio completo o acceder a tus conversaciones privadas.",
    signals:["Archivos encriptados o bloqueados","Mensaje de 'paga para recuperar'","Acceso extraño a cuentas"],
    color: MP.crimsonBright, accent:"rgba(168,16,48,0.3)" },
  { id:6, icon:"🎭", tag:"TIPO-F", title:"INGENIERÍA SOCIAL", greek:"Κοινωνική μηχανική",
    desc:"Se hacen pasar por tu banco, gobierno, o incluso por Delta Protec. Manipulan tu confianza para extraer datos, claves o transferencias.",
    signals:["Piden datos por 'verificación'","Usan logos y nombres reales","Presionan con urgencia falsa"],
    color: MP.goldBright, accent:"rgba(255,160,40,0.25)" },
];

// ── Pasos operativo Delta ──────────────────────────────────────────────────────
const DELTA_STEPS = [
  { n:"01", title:"CONTACTO CIFRADO",    greek:"Επικοινωνία", icon:"🔐", desc:"Nos escribes por canal seguro. Evaluamos tu caso en tiempo real sin burocracia." },
  { n:"02", title:"VERIFICACIÓN 1A1",    greek:"Επαλήθευση",  icon:"👁",  desc:"Videollamada encriptada obligatoria. Confirmamos tu identidad y la veracidad del caso." },
  { n:"03", title:"ANÁLISIS DE AMENAZA", greek:"Ανάλυση",     icon:"🧬", desc:"Diseccionamos la evidencia. Rastreamos el origen, infraestructura y perfil del agresor." },
  { n:"04", title:"OPERATIVO ACTIVO",    greek:"Επιχείρηση",  icon:"⚡", desc:"Desplegamos protocolos de interceptación, indexación y neutralización de la amenaza." },
  { n:"05", title:"ENTREGA DE REPORTE",  greek:"Αποτελέσματα",icon:"📁", desc:"Recibes expediente completo con evidencia forense lista para presentar ante autoridades." },
];

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQS = [
  { q:"¿Qué hago si ya pagué una vez?", a:"No pagues más. Que hayas pagado una vez no te obliga a pagar de nuevo — al contrario, eso confirma que son extorsionadores y tenemos más argumento legal. Contáctanos inmediatamente con el comprobante de transferencia." },
  { q:"¿Qué pasa si ya publicaron algo?", a:"La situación tiene solución. Contamos con protocolos de eliminación de contenido y rastreo retroactivo. Actuar rápido aumenta las probabilidades de contención total." },
  { q:"¿Esto tiene costo?", a:"La atención en la Zona Segura es GRATUITA. Si tu caso requiere un operativo de alto nivel, te informamos antes de cualquier acción. Nunca cobramos sin tu autorización." },
  { q:"¿Puedo confiar en que mis datos están seguros?", a:"Confidencialidad de Búnker: tu historia y tus datos mueren con nosotros. Operamos bajo encriptación militar y protocolo Zero Indexing — nada queda almacenado." },
  { q:"¿Qué pasa si el extorsionador está en otro país?", a:"La frontera no existe en el mundo digital. Nuestros operativos cruzan jurisdicciones y rastreamos nodos internacionales. Ubicación geográfica no es obstáculo." },
  { q:"¿Puedo reportar el caso a la policía también?", a:"Sí, y te ayudamos a preparar la denuncia. Nuestro reporte forense es evidencia válida ante Ministerio Público, FGR y CONDUSEF dependiendo del tipo de caso." },
];

// ── Señales de alerta ──────────────────────────────────────────────────────────
const WARNING_SIGNS = [
  { icon:"👤", text:"Alguien desconocido te contactó con demasiado interés y urgencia" },
  { icon:"🔗", text:"Recibiste un enlace extraño que abriste sin verificar" },
  { icon:"📧", text:"Te llegó un correo con tus datos personales reales expuestos" },
  { icon:"💬", text:"Un 'agente' te pide datos bancarios por WhatsApp o Telegram" },
  { icon:"📞", text:"Recibes llamadas de números desconocidos que cortan al contestar" },
  { icon:"🌐", text:"Notas actividad extraña en tus redes sociales o correo" },
];

// ══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTES ATMOSFÉRICOS
// ══════════════════════════════════════════════════════════════════════════════

/** Scanlines CRT sobre toda la página */
function ScanlineOverlay() {
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      pointerEvents: "none", zIndex: 5,
      backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.07) 2px, rgba(0,0,0,0.07) 4px)",
      mixBlendMode: "overlay",
    }} />
  );
}

/** Ticker de alertas desplazándose */
function AlertTicker() {
  const full = [...TICKER_ALERTS, ...TICKER_ALERTS];
  return (
    <div style={{ overflow: "hidden", borderBottom: "1px solid rgba(107,0,21,0.4)",
      background: "rgba(4,0,2,0.96)", height: 28, display: "flex", alignItems: "center",
      position: "relative", zIndex: 20 }}>
      {/* borde izquierdo rojo */}
      <div style={{ flexShrink:0, padding:"0 12px", borderRight:"1px solid rgba(107,0,21,0.5)",
        fontFamily: DT.terminal, fontSize:"9px", color: MP.crimsonBright, letterSpacing:"0.2em",
        whiteSpace:"nowrap" }}>
        ALERTA VIVA
      </div>
      <div style={{ overflow:"hidden", flex:1, maskImage:"linear-gradient(90deg,transparent,black 40px,black calc(100% - 40px),transparent)" }}>
        <motion.div
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 55, repeat: Infinity, ease: "linear" }}
          style={{ display:"flex", gap:64, whiteSpace:"nowrap" }}>
          {full.map((t, i) => (
            <span key={i} style={{ fontFamily: DT.terminal, fontSize:"9px",
              color: i % 3 === 0 ? MP.crimsonBright : "rgba(255,255,255,0.55)",
              letterSpacing:"0.18em" }}>{t}</span>
          ))}
        </motion.div>
      </div>
      <motion.div animate={{ opacity:[0.4,1,0.4] }} transition={{ duration:1.2, repeat:Infinity }}
        style={{ flexShrink:0, padding:"0 12px", borderLeft:"1px solid rgba(107,0,21,0.5)",
          fontFamily: DT.terminal, fontSize:"9px", color:"rgba(76,170,96,0.8)", letterSpacing:"0.15em" }}>
        ● EN VIVO
      </motion.div>
    </div>
  );
}

/** Barra de conexión segura */
function SecureConnectionBar() {
  const [time, setTime] = useState(new Date());
  const [encKey] = useState(() => Math.random().toString(36).substring(2,10).toUpperCase());
  const [nodeId] = useState(() => `NODE-${Math.floor(Math.random()*999).toString().padStart(3,"0")}`);
  useEffect(() => {
    const iv = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  return (
    <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.6, delay:0.4 }}
      style={{ maxWidth:1300, margin:"0 auto", padding:"0 18px 0" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8,
        padding:"7px 16px", background:"rgba(4,0,2,0.92)", border:"1px solid rgba(107,0,21,0.3)",
        borderTop:"none" }}>
        {/* Estado conexión */}
        <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <motion.div animate={{ opacity:[0.5,1,0.5] }} transition={{ duration:1.5, repeat:Infinity }}
              style={{ width:5, height:5, borderRadius:"50%", background:"#9b00ff", boxShadow:"0 0 7px rgba(76,170,96,0.7)" }} />
            <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(76,170,96,0.75)", letterSpacing:"0.2em" }}>CONEXIÓN SEGURA ACTIVA</span>
          </div>
          <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,255,255,0.3)", letterSpacing:"0.1em" }}>|</span>
          <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,255,255,0.82)", letterSpacing:"0.15em" }}>ENC: AES-256-GCM · {encKey}</span>
          <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,255,255,0.3)", letterSpacing:"0.1em" }}>|</span>
          <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,255,255,0.82)", letterSpacing:"0.15em" }}>IP: [ENMASCARADA] · {nodeId}</span>
        </div>
        {/* Reloj */}
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,160,40,0.82)", letterSpacing:"0.2em" }}>
            {time.toLocaleTimeString("es-MX", { hour12:false })} · {time.toLocaleDateString("es-MX")}
          </span>
          <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,255,255,0.3)", letterSpacing:"0.1em" }}>|</span>
          <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,160,40,0.72)", letterSpacing:"0.2em" }}>ZERO-INDEX ACTIVO</span>
        </div>
      </div>
    </motion.div>
  );
}

/** Nivel de amenaza y stats en vivo */
function ThreatLevelBar() {
  const [cases, setCases]       = useState(441);
  const [neutral, setNeutral]   = useState(389);
  const [response, setResponse] = useState(4);

  useEffect(() => {
    const iv = setInterval(() => {
      if (Math.random() > 0.75) setCases(c => c + 1);
      if (Math.random() > 0.85) setNeutral(n => n + 1);
      setResponse(Math.floor(2 + Math.random() * 6));
    }, 3000);
    return () => clearInterval(iv);
  }, []);

  const threatSegments = [
    { label:"BAJO",   color:"rgba(76,170,96,0.7)",   w:"20%" },
    { label:"MEDIO",  color:"rgba(200,160,40,0.75)",  w:"20%" },
    { label:"ALTO",   color:"rgba(220,100,20,0.8)",   w:"20%" },
    { label:"CRÍTICO",color:"rgba(168,16,48,0.9)",    w:"20%" },
    { label:"OMEGA",  color:"rgba(107,0,21,1)",        w:"20%" },
  ];

  return (
    <motion.div initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7, delay:0.6 }}
      style={{ maxWidth:1300, margin:"0 auto", padding:"0 18px" }}>
      <div style={{ padding:"12px 20px", background:"rgba(4,0,2,0.9)", border:"1px solid rgba(107,0,21,0.3)",
        borderTop:"none", display:"flex", alignItems:"center", gap:24, flexWrap:"wrap" }}>

        {/* Nivel de amenaza */}
        <div style={{ flex:"1 1 280px", minWidth:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
            <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,255,255,0.88)", letterSpacing:"0.25em" }}>NIVEL DE AMENAZA ACTIVA</span>
            <motion.span animate={{ opacity:[0.6,1,0.6] }} transition={{ duration:1.2, repeat:Infinity }}
              style={{ fontFamily:DT.terminal, fontSize:"9px", color:MP.crimsonBright, letterSpacing:"0.2em" }}>CRÍTICO</motion.span>
          </div>
          <div style={{ display:"flex", gap:2, height:6, borderRadius:1, overflow:"hidden" }}>
            {threatSegments.map((s,i) => (
              <motion.div key={i} initial={{ scaleX:0 }} animate={{ scaleX:1 }}
                transition={{ duration:0.4, delay:0.8 + i*0.15 }}
                style={{ flex:1, background:s.color, transformOrigin:"left",
                  boxShadow: i >= 3 ? `0 0 8px ${s.color}` : "none" }} />
            ))}
          </div>
          {/* Indicador */}
          <div style={{ position:"relative", height:10 }}>
            <motion.div animate={{ left:["72%","76%","72%"] }} transition={{ duration:3, repeat:Infinity, ease:"easeInOut" }}
              style={{ position:"absolute", top:2, width:2, height:6, background:MP.crimsonBright,
                boxShadow:"0 0 8px rgba(168,16,48,0.9)", borderRadius:1 }} />
          </div>
        </div>

        {/* Separador */}
        <div style={{ width:1, height:40, background:"rgba(107,0,21,0.3)", flexShrink:0 }} />

        {/* Stats en vivo */}
        {[
          { label:"CASOS ACTIVOS", value:cases, color:MP.crimsonBright, blink:true },
          { label:"NEUTRALIZADOS", value:neutral, color:"rgba(76,170,96,0.8)", blink:false },
          { label:"T. RESPUESTA (MIN)", value:response, color:MP.goldBright, blink:false },
          { label:"PAÍSES MONITOREADOS", value:18, color:"rgba(255,255,255,0.7)", blink:false },
        ].map((s,i) => (
          <div key={i} style={{ textAlign:"center", minWidth:80 }}>
            <motion.p
              animate={s.blink ? { opacity:[0.7,1,0.7] } : undefined}
              transition={{ duration:1.5, repeat:Infinity }}
              style={{ fontFamily:DT.terminal, fontSize:"clamp(0.9rem, 2vw, 1.3rem)", color:s.color,
                letterSpacing:"0.05em", lineHeight:1 }}>
              {s.value.toString().padStart(s.value > 99 ? 3 : 2,"0")}
            </motion.p>
            <p style={{ fontFamily:DT.terminal, fontSize:"7px", color:"rgba(255,255,255,0.78)", letterSpacing:"0.2em", marginTop:3 }}>{s.label}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/** Título con glitch */
function GlitchTitle({ text, style }) {
  const [glitching, setGlitching] = useState(false);
  useEffect(() => {
    const fire = () => {
      setGlitching(true);
      setTimeout(() => setGlitching(false), 160);
      setTimeout(fire, 3500 + Math.random() * 5000);
    };
    const t = setTimeout(fire, 1500 + Math.random() * 2000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ position:"relative", display:"inline-block" }}>
      <motion.h3
        animate={glitching ? { x:[0,-3,3,-2,2,0], skewX:[0,-4,4,-2,2,0], opacity:[1,0.6,1,0.8,1] } : undefined}
        transition={{ duration:0.16 }}
        style={{ ...style, position:"relative", zIndex:2 }}>
        {text}
      </motion.h3>
      {glitching && (
        <>
          <h3 style={{ ...style, position:"absolute", top:2, left:3, color:"rgba(168,16,48,0.5)", zIndex:1, userSelect:"none" }}>{text}</h3>
          <h3 style={{ ...style, position:"absolute", top:-2, left:-3, color:"rgba(0,200,255,0.15)", zIndex:1, userSelect:"none" }}>{text}</h3>
        </>
      )}
    </div>
  );
}

/** Sello CLASIFICADO girado */
function ClassifiedStamp() {
  return (
    <div style={{ position:"absolute", top:22, left:18, transform:"rotate(-18deg)",
      border:"3px solid rgba(107,0,21,0.65)", padding:"4px 10px", pointerEvents:"none", zIndex:10 }}>
      <p style={{ fontFamily:DT.decree, fontSize:"clamp(0.55rem, 1.2vw, 0.7rem)", color:"rgba(107,0,21,0.65)",
        letterSpacing:"0.35em", lineHeight:1.2, textAlign:"center" }}>
        CLASIFICADO<br/>CEO DIRECTO
      </p>
    </div>
  );
}

/** Radar animado (detrás del logo) */
function RadarPulse() {
  return (
    <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", pointerEvents:"none" }}>
      {[1,2,3].map(i => (
        <motion.div key={i}
          animate={{ scale:[0.5,2.2], opacity:[0.35,0] }}
          transition={{ duration:3, repeat:Infinity, delay:i * 0.9, ease:"easeOut" }}
          style={{ position:"absolute", top:"50%", left:"50%",
            width:90, height:90, borderRadius:"50%",
            border:"1px solid rgba(107,0,21,0.6)",
            transform:"translate(-50%,-50%)" }} />
      ))}
      {/* Línea de barrido */}
      <motion.div animate={{ rotate:[0,360] }} transition={{ duration:4, repeat:Infinity, ease:"linear" }}
        style={{ position:"absolute", top:"50%", left:"50%", width:80, height:1,
          transformOrigin:"0 50%",
          background:"linear-gradient(90deg, rgba(107,0,21,0.8), transparent)" }} />
    </div>
  );
}

// ── ProtocolCard ───────────────────────────────────────────────────────────────
function ProtocolCard({ p, delay }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.5, delay }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ border:`1px solid ${p.border}`, background: hovered ? "rgba(10,6,18,0.97)" : "rgba(8,4,14,0.88)",
        boxShadow: hovered ? `0 0 28px ${p.glow}, 0 0 60px rgba(0,0,0,0.9), inset 0 0 20px ${p.glow}` : `0 0 14px ${p.glow}, 0 0 6px rgba(0,0,0,0.8)`,
        transition:"all 0.3s ease", borderRadius:2, position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", top:0, left:0, width:22, height:22,
        borderTop:`2px solid ${p.color}`, borderLeft:`2px solid ${p.color}`, opacity:0.65 }} />
      <div style={{ position:"absolute", bottom:0, right:0, width:14, height:14,
        borderBottom:`1px solid ${p.color}`, borderRight:`1px solid ${p.color}`, opacity:0.35 }} />
      <div style={{ padding:"14px 14px 13px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:7 }}>
          <span style={{ fontFamily:DT.terminal, fontSize:"11px", color:p.color, letterSpacing:DL.terminal, opacity:0.75,
            textShadow:`0 0 8px ${p.color}` }}>[ {p.code} ]</span>
          <span style={{ fontSize:"16px", filter:`drop-shadow(0 0 6px ${p.color}) drop-shadow(0 0 14px ${p.color})` }}>{p.icon}</span>
        </div>
        <p style={{ fontFamily:DT.decree, fontSize:"12px", color:p.color, letterSpacing:DL.decree, marginBottom:3, lineHeight:1.25,
          textShadow:`0 0 12px ${p.color}, 0 0 28px ${p.color}, 0 0 50px ${p.glow}` }}>{p.title}</p>
        <p style={{ fontFamily:DT.greek, fontSize:"10px", color:"rgba(255,255,255,0.5)", letterSpacing:DL.greek, marginBottom:9, fontStyle:"italic",
          textShadow:`0 0 10px ${p.glow}` }}>{p.subtitle}</p>
        <div style={{ height:"1px", background:`linear-gradient(90deg, ${p.border}, transparent)`, marginBottom:9,
          boxShadow:`0 0 8px ${p.glow}` }} />
        <p style={{ fontFamily:DT.notebook, fontSize:"11px", color:"rgba(255,255,255,0.78)", lineHeight:1.55, letterSpacing:DL.notebook,
          textShadow:`0 0 15px ${p.glow}` }}>{p.body}</p>
      </div>
    </motion.div>
  );
}

function CardColumn({ cards, delayBase }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12, flex:"1 1 0", minWidth:0 }}>
      {cards.map((p,i) => <ProtocolCard key={p.id} p={p} delay={delayBase + i*0.12} />)}
    </div>
  );
}

function SectionTitle({ label, greek, title, sub }) {
  return (
    <div style={{ textAlign:"center", marginBottom:32 }}>
      <motion.div initial={{ scaleX:0 }} whileInView={{ scaleX:1 }} viewport={{ once:true }} transition={{ duration:1 }}
        style={{ height:"1px", background:"linear-gradient(90deg, transparent, rgba(107,0,21,0.55), rgba(255,160,40,0.45), rgba(107,0,21,0.55), transparent)", marginBottom:16,
        boxShadow:"0 0 12px rgba(107,0,21,0.3)" }} />
      {greek && <p style={{ fontFamily:DT.greek, fontSize:"11px", color:"rgba(255,160,40,0.88)", letterSpacing:DL.greek, marginBottom:6, fontStyle:"italic",
        textShadow:"0 0 14px rgba(255,160,40,0.5)" }}>{greek}</p>}
      <p style={{ fontFamily:DT.terminal, fontSize:"10px", color:"rgba(255,160,40,0.85)", letterSpacing:"0.4em", marginBottom:8,
        textShadow:"0 0 10px rgba(255,160,40,0.4)" }}>{label}</p>
      <h2 style={{ fontFamily:DT.decree, fontSize:"clamp(0.75rem, 2vw, 1.05rem)", color:MP.goldBright, letterSpacing:"0.22em", marginBottom: sub ? 8 : 0,
        textShadow:"0 0 20px rgba(255,160,40,0.6), 0 0 50px rgba(255,160,40,0.25)" }}>{title}</h2>
      {sub && <p style={{ fontFamily:DT.notebook, fontSize:"clamp(0.62rem, 1.3vw, 0.76rem)", color:"rgba(255,255,255,0.9)", letterSpacing:DL.notebook, maxWidth:520, margin:"0 auto", lineHeight:1.5 }}>{sub}</p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PÁGINA PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
export function BunkerPage() {
  const [msgIndex, setMsgIndex] = useState(0);
  const [openFaq, setOpenFaq]   = useState(null);

  useEffect(() => {
    const iv = setInterval(() => setMsgIndex(i => (i + 1) % CALMING_MESSAGES.length), 3800);
    return () => clearInterval(iv);
  }, []);

  const currentMsg  = CALMING_MESSAGES[msgIndex];
  const leftCards   = PROTOCOLS.slice(0, 3);
  const rightCards  = PROTOCOLS.slice(3, 6);
  const bottomCards = PROTOCOLS.slice(6, 8);

  return (
    <div style={{ minHeight:"100vh", background:MP.abyss, position:"relative", overflowX:"hidden" }}>
      <MysticalBg />
      <ScanlineOverlay />

      {/* Botón de retroceso */}
      <BackButton />

      {/* Header */}
      <div style={{ position:"relative", zIndex:30 }}><SiteHeader /></div>

      {/* ── TICKER ────────────────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:20 }}>
        <AlertTicker />
      </div>

      {/* ── CONEXIÓN SEGURA ───────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:15 }}>
        <SecureConnectionBar />
        <ThreatLevelBar />
      </div>

      {/* ── HERO TITLE ────────────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, textAlign:"center", paddingTop:"28px", paddingBottom:"8px" }}>
        <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ duration:1.2, delay:0.2 }}
          style={{ fontFamily:DT.terminal, fontSize:"11px", color:"rgba(255,160,40,0.88)", letterSpacing:"0.35em", marginBottom:8 }}>
          ⬡ ZONA SEGURA · PROTOCOLO DE EMERGENCIA · ΔΕΛΤΑ ΠΡΟΤΕΚ ⬡
        </motion.p>
        <motion.div initial={{ opacity:0, y:-14 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.9, delay:0.35 }}>
          <GlitchTitle text="ΚΑΤΑΦΥΓΙΟ ΥΨΗΛΗΣ ΙΣΧΥΟΣ"
            style={{ fontFamily:DT.greek, fontSize:"clamp(1.3rem, 3.5vw, 2.4rem)", color:MP.goldBright,
              letterSpacing:"0.18em", textShadow:"0 0 30px rgba(255,160,40,0.35), 0 0 80px rgba(107,0,21,0.2)",
              lineHeight:1.2, marginBottom:4 }} />
        </motion.div>
        <motion.p initial={{ opacity:0 }} animate={{ opacity:0.75 }} transition={{ duration:1, delay:0.6 }}
          style={{ fontFamily:DT.decree, fontSize:"clamp(0.5rem, 1.2vw, 0.72rem)", color:"rgba(255,160,40,0.65)", letterSpacing:"0.3em" }}>
          BÚNKER ALTO IMPACTO
        </motion.p>
        <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }} transition={{ duration:1.1, delay:0.8 }}
          style={{ margin:"14px auto 0", height:"1px", width:"min(500px, 80vw)",
            background:"linear-gradient(90deg, transparent, rgba(107,0,21,0.6), rgba(255,160,40,0.5), rgba(107,0,21,0.6), transparent)" }} />
      </div>

      {/* ── LAYOUT ORBITAL ────────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, maxWidth:1300, margin:"0 auto", padding:"24px 18px 0" }}>
        <div style={{ display:"flex", gap:16, alignItems:"center" }}>
          <CardColumn cards={leftCards} delayBase={0.5} />

          {/* CENTRO */}
          <div style={{ flex:"0 0 clamp(260px, 32vw, 420px)", display:"flex", flexDirection:"column", alignItems:"center" }}>
            {/* Logo con radar */}
            <div style={{ position:"relative", marginBottom:18 }}>
              <RadarPulse />
              <motion.div
                animate={{ filter:["drop-shadow(0 0 12px rgba(107,0,21,0.55)) drop-shadow(0 0 40px rgba(80,0,10,0.25))","drop-shadow(0 0 24px rgba(107,0,21,0.85)) drop-shadow(0 0 70px rgba(80,0,10,0.45))","drop-shadow(0 0 12px rgba(107,0,21,0.55)) drop-shadow(0 0 40px rgba(80,0,10,0.25))"], scale:[1,1.03,1] }}
                transition={{ duration:4, repeat:Infinity, ease:"easeInOut" }}>
                <img src={logoImg} alt="Delta Protec Logo"
                  style={{ width:"clamp(100px, 14vw, 160px)", height:"clamp(100px, 14vw, 160px)", objectFit:"contain", mixBlendMode:"lighten", position:"relative", zIndex:2 }} />
              </motion.div>
            </div>

            {/* Panel central de mensajes */}
            <div style={{ width:"100%", border:"1px solid rgba(107,0,21,0.35)", background:"rgba(6,4,12,0.85)",
              backdropFilter:"blur(14px)", boxShadow:"0 0 40px rgba(107,0,21,0.12), 0 0 80px rgba(0,0,0,0.9)",
              borderRadius:2, padding:"18px 20px 20px", position:"relative", overflow:"hidden", textAlign:"center" }}>
              {[{t:0,l:0,bt:true,bl:true},{t:0,r:0,bt:true,br:true},{b:0,l:0,bb:true,bl:true},{b:0,r:0,bb:true,br:true}]
                .map((c:any, i) => (
                  <div key={i} style={{ position:"absolute", width:14, height:14,
                    top:c.t??undefined, bottom:c.b??undefined, left:c.l??undefined, right:c.r??undefined,
                    borderTop:c.bt?"1px solid rgba(255,160,40,0.5)":undefined,
                    borderLeft:c.bl?"1px solid rgba(255,160,40,0.5)":undefined,
                    borderBottom:c.bb?"1px solid rgba(255,160,40,0.5)":undefined,
                    borderRight:c.br?"1px solid rgba(255,160,40,0.5)":undefined }} />
                ))}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:7, marginBottom:13 }}>
                <motion.div animate={{ opacity:[0.4,1,0.4] }} transition={{ duration:1.5, repeat:Infinity }}
                  style={{ width:6, height:6, borderRadius:"50%", background:"#9b00ff", boxShadow:"0 0 8px rgba(76,170,96,0.6)" }} />
                <span style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(76,170,96,0.8)", letterSpacing:"0.28em" }}>
                  BÚNKER EN LÍNEA · ZONA SEGURA ACTIVA
                </span>
              </div>
              <AnimatePresence mode="wait">
                <motion.p key={`greek-${msgIndex}`} initial={{ opacity:0,y:8 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0,y:-8 }} transition={{ duration:0.32 }}
                  style={{ fontFamily:DT.greek, fontSize:"clamp(0.85rem, 2vw, 1.15rem)", color:MP.goldBright,
                    letterSpacing:DL.greek, marginBottom:8, textShadow:"0 0 20px rgba(255,160,40,0.3)" }}>
                  {currentMsg.greek}
                </motion.p>
              </AnimatePresence>
              <div style={{ width:50, height:"1px", margin:"0 auto 9px", background:"linear-gradient(90deg, transparent, rgba(107,0,21,0.6), transparent)" }} />
              <AnimatePresence mode="wait">
                <motion.p key={`es-${msgIndex}`} initial={{ opacity:0 }} animate={{ opacity:0.85 }} exit={{ opacity:0 }} transition={{ duration:0.4,delay:0.08 }}
                  style={{ fontFamily:DT.notebook, fontSize:"clamp(0.65rem, 1.5vw, 0.82rem)", color:"rgba(255,255,255,0.82)", letterSpacing:DL.notebook, marginBottom:13 }}>
                  {currentMsg.spanish}
                </motion.p>
              </AnimatePresence>
              <div style={{ display:"flex", justifyContent:"center", gap:5 }}>
                {CALMING_MESSAGES.map((_,i) => (
                  <motion.div key={i} animate={{ background:i===msgIndex?MP.crimsonBright:"rgba(107,0,21,0.25)", boxShadow:i===msgIndex?"0 0 8px rgba(168,16,48,0.6)":"none" }}
                    transition={{ duration:0.3 }} onClick={() => setMsgIndex(i)}
                    style={{ width:i===msgIndex?16:5, height:3, borderRadius:2, cursor:"pointer", transition:"width 0.3s ease" }} />
                ))}
              </div>
            </div>

            <motion.div animate={{ y:[0,-6,0] }} transition={{ duration:3.5, repeat:Infinity, ease:"easeInOut" }} style={{ marginTop:16 }}>
              <BotLogo size={48} style={{ opacity:0.7 }} />
            </motion.div>
            <motion.p animate={{ opacity:[0.3,0.6,0.3] }} transition={{ duration:2.5, repeat:Infinity }}
              style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,160,40,0.85)", letterSpacing:"0.2em", marginTop:7 }}>
              OMNISCIENT OBSERVER · ACTIVO
            </motion.p>
          </div>

          <CardColumn cards={rightCards} delayBase={0.7} />
        </div>

        {/* Fila inferior */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2, minmax(0,1fr))", gap:14,
          maxWidth:700, margin:"14px auto 0" }}>
          {bottomCards.map((p,i) => <ProtocolCard key={p.id} p={p} delay={1.2+i*0.12} />)}
        </div>
      </div>

      {/* ── SEÑALES DE ALERTA TEMPRANA ────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, maxWidth:1200, margin:"60px auto 0", padding:"0 18px" }}>
        <SectionTitle label="⬡ RECONOCE EL PELIGRO ANTES DE QUE TE ALCANCE ⬡"
          greek="Σήματα κινδύνου" title="SEÑALES DE ALERTA TEMPRANA"
          sub="Si identificas alguna de estas señales, actúa antes de que la situación escale. El tiempo corre en tu contra." />
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:12 }}>
          {WARNING_SIGNS.map((w, i) => (
            <motion.div key={i} initial={{ opacity:0, x:i%2===0?-16:16 }} whileInView={{ opacity:1,x:0 }}
              viewport={{ once:true }} transition={{ duration:0.5, delay:i*0.08 }}
              style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"14px 16px",
                border:"1px solid rgba(107,0,21,0.3)", borderLeft:`3px solid rgba(168,16,48,0.6)`,
                background:"rgba(8,4,14,0.7)", borderRadius:2,
                boxShadow:"0 0 16px rgba(107,0,21,0.1), inset 0 0 12px rgba(0,0,0,0.4)" }}>
              <span style={{ fontSize:"20px", lineHeight:1, flexShrink:0,
                filter:"drop-shadow(0 0 6px rgba(168,16,48,0.7)) drop-shadow(0 0 14px rgba(107,0,21,0.5))" }}>{w.icon}</span>
              <p style={{ fontFamily:DT.notebook, fontSize:"12px", color:"rgba(255,255,255,0.82)", lineHeight:1.5, letterSpacing:DL.notebook,
                textShadow:"0 0 12px rgba(107,0,21,0.3)" }}>{w.text}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── TIPOS DE EXTORSIÓN ────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, maxWidth:1200, margin:"60px auto 0", padding:"0 18px" }}>
        <SectionTitle label="⬡ IDENTIFICACIÓN DE AMENAZA ⬡"
          greek="Τύποι εκβιασμού" title="TIPOS DE EXTORSIÓN — RECONOCE TU CASO"
          sub="Cada tipo tiene su propio protocolo de respuesta. Identificar el tuyo acelera el operativo." />
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:14 }}>
          {EXTORTION_TYPES.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity:0, y:18 }} whileInView={{ opacity:1,y:0 }}
              viewport={{ once:true }} transition={{ duration:0.55, delay:i*0.1 }}
              style={{ border:`1px solid rgba(168,16,48,0.25)`, background:"rgba(8,4,14,0.88)",
                borderRadius:2, overflow:"hidden", position:"relative" }}>
              <div style={{ height:2, background:`linear-gradient(90deg, ${t.color}, transparent)` }} />
              <div style={{ padding:"16px 16px 18px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                  <span style={{ fontSize:"22px",
                    filter:`drop-shadow(0 0 7px ${t.color}) drop-shadow(0 0 20px ${t.color})` }}>{t.icon}</span>
                  <div>
                    <span style={{ fontFamily:DT.terminal, fontSize:"10px", color:"rgba(255,160,40,0.82)", letterSpacing:"0.3em", display:"block",
                      textShadow:"0 0 8px rgba(255,160,40,0.4)" }}>[ {t.tag} ]</span>
                    <p style={{ fontFamily:DT.decree, fontSize:"13px", color:t.color, letterSpacing:DL.decree, lineHeight:1.2,
                      textShadow:`0 0 10px ${t.color}, 0 0 26px ${t.color}, 0 0 55px ${t.accent}` }}>{t.title}</p>
                  </div>
                </div>
                <p style={{ fontFamily:DT.greek, fontSize:"10px", color:"rgba(255,255,255,0.82)", fontStyle:"italic", letterSpacing:DL.greek, marginBottom:10,
                  textShadow:`0 0 12px ${t.accent}` }}>{t.greek}</p>
                <p style={{ fontFamily:DT.notebook, fontSize:"12px", color:"rgba(255,255,255,0.92)", lineHeight:1.55, letterSpacing:DL.notebook, marginBottom:12,
                  textShadow:"0 0 18px rgba(0,0,0,0.9)" }}>{t.desc}</p>
                <div style={{ borderTop:`1px solid ${t.color}22`, paddingTop:10,
                  boxShadow:`0 -1px 14px ${t.accent}` }}>
                  <p style={{ fontFamily:DT.terminal, fontSize:"9px", color:t.color, opacity:0.6, letterSpacing:"0.25em", marginBottom:7,
                    textShadow:`0 0 10px ${t.color}, 0 0 22px ${t.accent}` }}>SEÑALES IDENTIFICADAS:</p>
                  {t.signals.map((s,si) => (
                    <div key={si} style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:5 }}>
                      <span style={{ color:t.color, fontSize:"10px", marginTop:1, flexShrink:0,
                        textShadow:`0 0 8px ${t.color}, 0 0 20px ${t.color}` }}>▸</span>
                      <p style={{ fontFamily:DT.terminal, fontSize:"10px", color:"rgba(255,255,255,0.75)", letterSpacing:"0.05em", lineHeight:1.4,
                        textShadow:`0 0 10px ${t.accent}` }}>{s}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── ¿YA PAGASTE? ──────────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, maxWidth:900, margin:"60px auto 0", padding:"0 18px" }}>
        <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1,y:0 }}
          viewport={{ once:true }} transition={{ duration:0.7 }}
          style={{ border:`1px solid rgba(168,16,48,0.55)`, background:"rgba(12,2,4,0.95)",
            borderRadius:2, padding:"30px 32px", position:"relative", overflow:"hidden",
            boxShadow:"0 0 60px rgba(107,0,21,0.2), inset 0 0 40px rgba(0,0,0,0.5)" }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:"2px",
            background:`linear-gradient(90deg, transparent, ${MP.crimsonBright}, ${MP.goldBright}, ${MP.crimsonBright}, transparent)` }} />
          <div style={{ position:"absolute", top:0, left:0, width:40, height:40,
            borderTop:`2px solid ${MP.crimsonBright}`, borderLeft:`2px solid ${MP.crimsonBright}`, opacity:0.7 }} />
          <div style={{ position:"absolute", bottom:0, right:0, width:40, height:40,
            borderBottom:`2px solid ${MP.goldBright}`, borderRight:`2px solid ${MP.goldBright}`, opacity:0.5 }} />

          <div style={{ textAlign:"center", marginBottom:24 }}>
            <motion.p animate={{ opacity:[0.6,1,0.6] }} transition={{ duration:2, repeat:Infinity }}
              style={{ fontFamily:DT.terminal, fontSize:"11px", color:MP.crimsonBright, letterSpacing:"0.4em", marginBottom:10 }}>
              ⚠ PROTOCOLO DE RESCATE TARDÍO ⚠
            </motion.p>
            <h3 style={{ fontFamily:DT.decree, fontSize:"clamp(0.9rem, 2.2vw, 1.3rem)", color:MP.goldBright, letterSpacing:"0.2em", marginBottom:10 }}>
              ¿YA PAGASTE? ¿YA CEDISTE?
            </h3>
            <p style={{ fontFamily:DT.notebook, fontSize:"clamp(0.65rem, 1.4vw, 0.8rem)", color:"rgba(255,255,255,0.75)",
              lineHeight:1.65, letterSpacing:DL.notebook, maxWidth:580, margin:"0 auto" }}>
              Escucha: que hayas pagado una vez no significa que terminó. Significa que acaban de confirmar
              que funcionas como objetivo. La presión va a aumentar. Pero también significa que tienes
              evidencia — y nosotros sabemos exactamente cómo usarla.
            </p>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))", gap:12 }}>
            {[
              { icon:"🧾", title:"GUARDA EL COMPROBANTE", text:"Ese recibo de transferencia es evidencia directa del delito. No lo borres bajo ninguna circunstancia." },
              { icon:"🚷", title:"CORTA EL CICLO YA", text:"No importa lo que te digan: no hagas un segundo pago. Cada transferencia adicional los fortalece y te debilita." },
              { icon:"⚖️", title:"TIENES CASO LEGAL", text:"El pago bajo amenaza es prueba de extorsión. Con nuestra evidencia forense, tienes caso ante FGR y Ministerio Público." },
              { icon:"🔇", title:"NO LO CUENTES AÚN", text:"No publiques lo que pasó en redes. La discreción protege la operación de rastreo que estamos a punto de ejecutar." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity:0,y:10 }} whileInView={{ opacity:1,y:0 }}
                viewport={{ once:true }} transition={{ duration:0.4, delay:i*0.1 }}
                style={{ padding:"14px 14px 16px", border:"1px solid rgba(107,0,21,0.35)",
                  background:"rgba(20,4,6,0.5)", borderRadius:2,
                  boxShadow:"0 0 18px rgba(107,0,21,0.12), inset 0 0 10px rgba(0,0,0,0.3)" }}>
                <span style={{ fontSize:"20px", display:"block", marginBottom:8,
                  filter:"drop-shadow(0 0 7px rgba(168,16,48,0.7)) drop-shadow(0 0 16px rgba(107,0,21,0.5))" }}>{item.icon}</span>
                <p style={{ fontFamily:DT.decree, fontSize:"11px", color:MP.crimsonBright, letterSpacing:DL.decree, marginBottom:6, lineHeight:1.3,
                  textShadow:`0 0 12px ${MP.crimsonBright}, 0 0 28px rgba(107,0,21,0.5)` }}>{item.title}</p>
                <p style={{ fontFamily:DT.notebook, fontSize:"11px", color:"rgba(255,255,255,0.9)", lineHeight:1.5, letterSpacing:DL.notebook }}>{item.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ── OPERATIVO 5 PASOS ─────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, maxWidth:1000, margin:"60px auto 0", padding:"0 18px" }}>
        <SectionTitle label="⬡ ASÍ TRABAJAMOS · TRANSPARENCIA TOTAL ⬡"
          greek="Επιχειρησιακή ακολουθία" title="OPERATIVO DELTA — 5 PASOS"
          sub="Cuando nos contactas, esto es exactamente lo que ocurre. Sin sorpresas, sin burocracia." />
        <div style={{ position:"relative" }}>
          <div style={{ position:"absolute", left:"50%", top:0, bottom:0, width:"1px",
            background:"linear-gradient(180deg, transparent, rgba(107,0,21,0.4), rgba(255,160,40,0.3), rgba(107,0,21,0.4), transparent)",
            transform:"translateX(-50%)" }} />
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            {DELTA_STEPS.map((step, i) => (
              <motion.div key={step.n} initial={{ opacity:0, x:i%2===0?-30:30 }}
                whileInView={{ opacity:1,x:0 }} viewport={{ once:true }} transition={{ duration:0.6, delay:i*0.1 }}
                style={{ display:"flex", alignItems:"center", gap:0, flexDirection:i%2===0?"row":"row-reverse" }}>
                <div style={{ flex:"1", padding:"16px 20px",
                  border:`1px solid ${i%2===0?"rgba(168,16,48,0.4)":"rgba(255,160,40,0.4)"}`,
                  background:"rgba(8,4,14,0.9)", borderRadius:2, position:"relative",
                  boxShadow:`0 0 20px ${i%2===0?"rgba(107,0,21,0.15)":"rgba(200,120,20,0.15)"}, inset 0 0 14px rgba(0,0,0,0.4)`,
                  marginRight:i%2===0?24:0, marginLeft:i%2===0?0:24 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                    <span style={{ fontFamily:DT.terminal, fontSize:"28px",
                      color:i%2===0?"rgba(168,16,48,0.6)":"rgba(255,160,40,0.5)", letterSpacing:"0.05em",
                      textShadow:`0 0 18px ${i%2===0?"rgba(168,16,48,0.5)":"rgba(255,160,40,0.4)"}` }}>{step.n}</span>
                    <span style={{ fontSize:"18px",
                      filter:`drop-shadow(0 0 6px ${i%2===0?"rgba(168,16,48,0.8)":"rgba(255,160,40,0.8)"}) drop-shadow(0 0 16px ${i%2===0?"rgba(107,0,21,0.5)":"rgba(200,120,20,0.5)"})`}}>{step.icon}</span>
                    <div>
                      <p style={{ fontFamily:DT.decree, fontSize:"12px", color:i%2===0?MP.crimsonBright:MP.goldBright, letterSpacing:DL.decree, lineHeight:1.2,
                        textShadow:`0 0 12px ${i%2===0?MP.crimsonBright:MP.goldBright}, 0 0 30px ${i%2===0?"rgba(107,0,21,0.4)":"rgba(200,120,20,0.4)"}` }}>{step.title}</p>
                      <p style={{ fontFamily:DT.greek, fontSize:"10px", color:"rgba(255,255,255,0.82)", letterSpacing:DL.greek, fontStyle:"italic" }}>{step.greek}</p>
                    </div>
                  </div>
                  <p style={{ fontFamily:DT.notebook, fontSize:"12px", color:"rgba(255,255,255,0.92)", lineHeight:1.55, letterSpacing:DL.notebook }}>{step.desc}</p>
                </div>
                <div style={{ flexShrink:0, width:14, height:14, borderRadius:"50%",
                  background:i%2===0?MP.crimsonBright:MP.goldBright,
                  boxShadow:`0 0 16px ${i%2===0?"rgba(168,16,48,0.9)":"rgba(255,160,40,0.9)"}, 0 0 32px ${i%2===0?"rgba(107,0,21,0.6)":"rgba(200,120,20,0.6)"}`,
                  zIndex:2 }} />
                <div style={{ flex:1 }} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── OMNISCIENT OBSERVER — ARMA ────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, maxWidth:760, margin:"60px auto 0", padding:"0 18px" }}>
        <motion.div initial={{ opacity:0, scale:0.97 }} whileInView={{ opacity:1, scale:1 }}
          viewport={{ once:true }} transition={{ duration:0.8 }}
          style={{ border:`1px solid rgba(107,0,21,0.7)`, background:"rgba(4,0,2,0.98)",
            borderRadius:2, padding:"36px 36px 32px", textAlign:"center", position:"relative", overflow:"hidden",
            boxShadow:"0 0 80px rgba(107,0,21,0.25), 0 0 160px rgba(0,0,0,0.99), inset 0 0 60px rgba(0,0,0,0.7)" }}>

          <ClassifiedStamp />

          {/* Línea superior pulsante */}
          <motion.div animate={{ opacity:[0.4,1,0.4] }} transition={{ duration:2.5, repeat:Infinity }}
            style={{ position:"absolute", top:0, left:0, right:0, height:"2px",
              background:`linear-gradient(90deg, transparent, ${MP.crimsonBright}, rgba(107,0,21,0.4), ${MP.crimsonBright}, transparent)` }} />

          {/* Esquinas crimson */}
          {[{t:0,l:0,bt:true,bl:true},{t:0,r:0,bt:true,br:true},{b:0,l:0,bb:true,bl:true},{b:0,r:0,bb:true,br:true}]
            .map((c:any,i) => (
              <div key={i} style={{ position:"absolute", width:28, height:28,
                top:c.t??undefined, bottom:c.b??undefined, left:c.l??undefined, right:c.r??undefined,
                borderTop:c.bt?`2px solid rgba(107,0,21,0.9)`:undefined,
                borderLeft:c.bl?`2px solid rgba(107,0,21,0.9)`:undefined,
                borderBottom:c.bb?`2px solid rgba(107,0,21,0.9)`:undefined,
                borderRight:c.br?`2px solid rgba(107,0,21,0.9)`:undefined }} />
            ))}

          {/* Sello CEO — esquina superior derecha */}
          <motion.div animate={{ opacity:[0.3,0.6,0.3] }} transition={{ duration:4, repeat:Infinity }}
            style={{ position:"absolute", top:18, right:22,
              fontFamily:DT.terminal, fontSize:"8px", color:"rgba(255,255,255,0.55)",
              letterSpacing:"0.22em", textAlign:"right", lineHeight:1.7 }}>
            ACCESO RESTRINGIDO<br/>NIVEL: CEO DIRECTO<br/>REF: OBS-OMEGA-001
          </motion.div>

          {/* Advertencia nivel máximo */}
          <motion.p animate={{ opacity:[0.55,1,0.55] }} transition={{ duration:1.8, repeat:Infinity }}
            style={{ fontFamily:DT.terminal, fontSize:"10px", color:MP.crimsonBright,
              letterSpacing:"0.5em", marginBottom:20 }}>
            ⬡ ADVERTENCIA DE NIVEL MÁXIMO ⬡
          </motion.p>

          {/* Glitch title */}
          <GlitchTitle text="OMNISCIENT OBSERVER"
            style={{ fontFamily:DT.greek, fontSize:"clamp(1.2rem, 3vw, 1.9rem)", color:"rgba(215,195,175,0.97)",
              letterSpacing:"0.15em", marginBottom:6,
              textShadow:"0 0 25px rgba(107,0,21,0.7), 0 0 60px rgba(80,0,10,0.5), 0 0 100px rgba(60,0,8,0.25)" }} />

          <p style={{ fontFamily:DT.decree, fontSize:"clamp(0.6rem, 1.3vw, 0.78rem)", color:MP.crimsonBright,
            letterSpacing:"0.35em", marginBottom:24,
            textShadow:"0 0 18px rgba(168,16,48,0.85), 0 0 40px rgba(107,0,21,0.5)" }}>
            NO ES UN JUEGO. ES UN ARMA.
          </p>

          <div style={{ width:90, height:"1px", margin:"0 auto 26px",
            background:"linear-gradient(90deg, transparent, rgba(107,0,21,0.85), transparent)" }} />

          <p style={{ fontFamily:DT.notebook, fontSize:"clamp(0.76rem, 1.5vw, 0.92rem)", color:"rgba(255,255,255,0.83)",
            lineHeight:1.9, letterSpacing:DL.notebook, maxWidth:560, margin:"0 auto 28px" }}>
            El sistema <strong style={{ color:"rgba(215,195,175,0.97)", letterSpacing:"0.08em" }}>OMNISCIENT OBSERVER</strong> es
            una arquitectura de vigilancia, rastreo e interceptación de alcance abismal.
            No fue diseñado para impresionar — fue diseñado para{" "}
            <strong style={{ color:MP.crimsonBright }}>neutralizar</strong>.
            Un despliegue mal calibrado puede generar daños colaterales irreversibles:
            comprometer equipos propios, exponer activos aliados y abrir vectores de
            contraataque que no tienen marcha atrás.
            Si estás leyendo esto, ya entiendes el peso de lo que hay aquí.
          </p>

          <div style={{ width:"100%", height:"1px", margin:"0 auto 26px",
            background:"linear-gradient(90deg, transparent, rgba(107,0,21,0.5), rgba(255,160,40,0.15), rgba(107,0,21,0.5), transparent)" }} />

          {/* Decreto CEO */}
          <motion.div initial={{ opacity:0 }} whileInView={{ opacity:1 }} viewport={{ once:true }}
            transition={{ duration:1, delay:0.4 }}
            style={{ padding:"18px 24px", border:"1px solid rgba(107,0,21,0.5)",
              background:"rgba(10,2,4,0.85)", borderRadius:2, maxWidth:520, margin:"0 auto 22px", position:"relative" }}>
            <div style={{ position:"absolute", top:0, left:0, width:16, height:16,
              borderTop:"1px solid rgba(255,160,40,0.45)", borderLeft:"1px solid rgba(255,160,40,0.45)" }} />
            <div style={{ position:"absolute", bottom:0, right:0, width:16, height:16,
              borderBottom:"1px solid rgba(255,160,40,0.45)", borderRight:"1px solid rgba(255,160,40,0.45)" }} />
            <p style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,160,40,0.88)",
              letterSpacing:"0.35em", marginBottom:12 }}>
              ⬡ DECRETO DE ADMINISTRACIÓN DIRECTA ⬡
            </p>
            <p style={{ fontFamily:DT.decree, fontSize:"clamp(0.65rem, 1.4vw, 0.82rem)", color:MP.goldPale,
              letterSpacing:"0.16em", lineHeight:1.85,
              textShadow:"0 0 16px rgba(255,160,40,0.4), 0 0 35px rgba(200,120,20,0.2)" }}>
              Esta zona es administrada directamente por el{" "}
              <strong style={{ color:MP.goldBright }}>CEO de Delta Protec</strong>.
              Ningún operativo se activa sin su autorización explícita.
              Ninguna solicitud aquí es menor.
              Ninguna acción aquí carece de consecuencias.
            </p>
          </motion.div>

          {/* Daño colateral */}
          <motion.div
            animate={{ borderColor:["rgba(107,0,21,0.25)","rgba(107,0,21,0.65)","rgba(107,0,21,0.25)"] }}
            transition={{ duration:3, repeat:Infinity }}
            style={{ display:"inline-flex", flexDirection:"column", alignItems:"center", gap:10,
              padding:"16px 26px", border:"1px solid rgba(107,0,21,0.35)",
              background:"rgba(15,2,4,0.65)", borderRadius:2, maxWidth:500, width:"100%" }}>
            <motion.span animate={{ opacity:[0.7,1,0.7] }} transition={{ duration:1.5, repeat:Infinity }}
              style={{ fontFamily:DT.terminal, fontSize:"10px", color:MP.crimsonBright, letterSpacing:"0.35em",
                textShadow:`0 0 14px ${MP.crimsonBright}, 0 0 30px rgba(107,0,21,0.5)` }}>
              ▲ RIESGO DE DAÑO COLATERAL CONFIRMADO ▲
            </motion.span>
            <p style={{ fontFamily:DT.notebook, fontSize:"11px", color:"rgba(255,255,255,0.9)",
              letterSpacing:DL.notebook, lineHeight:1.65, textAlign:"center", maxWidth:420 }}>
              El uso sin protocolo puede comprometer activos propios, exponer aliados
              e iniciar contraofensivas que el sistema no puede detener una vez lanzadas.
              Procede únicamente con autorización directa del CEO y comprensión total del alcance.
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, maxWidth:860, margin:"60px auto 0", padding:"0 18px" }}>
        <SectionTitle label="⬡ PREGUNTAS FRECUENTES ⬡"
          greek="Συχνές ερωτήσεις" title="LO QUE MÁS NOS PREGUNTAN"
          sub="Respuestas directas. Sin rodeos." />
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {FAQS.map((faq, i) => (
            <motion.div key={i} initial={{ opacity:0,y:10 }} whileInView={{ opacity:1,y:0 }}
              viewport={{ once:true }} transition={{ duration:0.4, delay:i*0.07 }}
              style={{ border:`1px solid ${openFaq===i?"rgba(107,0,21,0.55)":"rgba(107,0,21,0.22)"}`,
                background:openFaq===i?"rgba(14,4,8,0.95)":"rgba(8,4,12,0.8)",
                borderRadius:2, overflow:"hidden", transition:"all 0.3s ease",
                boxShadow:openFaq===i?"0 0 24px rgba(107,0,21,0.2), inset 0 0 16px rgba(0,0,0,0.4)":"0 0 8px rgba(107,0,21,0.08)" }}>
              <button onClick={() => setOpenFaq(openFaq===i?null:i)}
                style={{ width:"100%", padding:"14px 18px", background:"none", border:"none",
                  cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
                <span style={{ fontFamily:DT.decree, fontSize:"12px",
                  color:openFaq===i?MP.goldBright:"rgba(255,255,255,0.8)",
                  letterSpacing:DL.decree, textAlign:"left", lineHeight:1.4,
                  textShadow:openFaq===i?"0 0 16px rgba(255,160,40,0.5), 0 0 35px rgba(200,120,20,0.25)":"none" }}>{faq.q}</span>
                <motion.span animate={{ rotate:openFaq===i?45:0 }} transition={{ duration:0.25 }}
                  style={{ color:openFaq===i?MP.crimsonBright:"rgba(255,160,40,0.7)", fontSize:"18px", flexShrink:0,
                    textShadow:openFaq===i?`0 0 12px ${MP.crimsonBright}`:"none" }}>✦</motion.span>
              </button>
              <AnimatePresence>
                {openFaq===i && (
                  <motion.div initial={{ height:0,opacity:0 }} animate={{ height:"auto",opacity:1 }}
                    exit={{ height:0,opacity:0 }} transition={{ duration:0.3 }}
                    style={{ overflow:"hidden" }}>
                    <div style={{ padding:"0 18px 16px", borderTop:"1px solid rgba(107,0,21,0.2)" }}>
                      <p style={{ fontFamily:DT.notebook, fontSize:"12px", color:"rgba(255,255,255,0.92)",
                        lineHeight:1.65, letterSpacing:DL.notebook, paddingTop:12 }}>{faq.a}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── CTA CONTACTO ──────────────────────────────────────────────────────── */}
      <div style={{ position:"relative", zIndex:10, maxWidth:1200, margin:"0 auto", padding:"48px 18px 70px" }}>
        <motion.div initial={{ opacity:0,y:20 }} whileInView={{ opacity:1,y:0 }}
          viewport={{ once:true }} transition={{ duration:0.8 }}
          style={{ border:"1px solid rgba(107,0,21,0.5)", background:"rgba(8,2,6,0.9)",
            boxShadow:"0 0 60px rgba(107,0,21,0.15), 0 0 120px rgba(0,0,0,0.95)",
            borderRadius:2, padding:"30px 28px", textAlign:"center", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:0, left:0, right:0, height:"2px",
            background:`linear-gradient(90deg, transparent, ${MP.crimsonBright}, ${MP.goldBright}, ${MP.crimsonBright}, transparent)` }} />
          <motion.p animate={{ opacity:[0.5,0.9,0.5] }} transition={{ duration:3, repeat:Infinity }}
            style={{ fontFamily:DT.terminal, fontSize:"11px", color:"rgba(255,160,40,0.9)", letterSpacing:"0.4em", marginBottom:10 }}>
            ⬡ ACCESO DIRECTO AL BÚNKER ⬡
          </motion.p>
          <p style={{ fontFamily:DT.decree, fontSize:"clamp(0.8rem, 2vw, 1.1rem)", color:MP.goldBright, letterSpacing:"0.2em", marginBottom:10 }}>
            ¿NECESITAS AYUDA INMEDIATA?
          </p>
          <p style={{ fontFamily:DT.notebook, fontSize:"clamp(0.62rem, 1.3vw, 0.78rem)", color:"rgba(255,255,255,0.92)",
            letterSpacing:DL.notebook, maxWidth:460, margin:"0 auto 22px", lineHeight:1.65 }}>
            La asistencia en la Zona Segura es totalmente gratuita. Escríbenos ahora mismo
            y un operativo Delta responde de inmediato. No estás solo/a.
          </p>
          <div style={{ display:"flex", justifyContent:"center", gap:14, flexWrap:"wrap" }}>
            <motion.a href="https://t.me/Unit_Intel_Delta_Bot" target="_blank" rel="noopener noreferrer"
              whileHover={{ scale:1.04, boxShadow:"0 0 38px rgba(107,0,21,0.75), 0 0 75px rgba(80,0,10,0.45)" }} whileTap={{ scale:0.97 }}
              style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"11px 24px",
                border:"1px solid rgba(107,0,21,0.65)", background:"rgba(20,2,4,0.6)",
                color:MP.crimsonBright, fontFamily:DT.terminal, fontSize:"12px", letterSpacing:"0.2em",
                textDecoration:"none", borderRadius:2, cursor:"pointer",
                boxShadow:`0 0 18px rgba(107,0,21,0.35), 0 0 40px rgba(80,0,10,0.2)`,
                textShadow:`0 0 10px ${MP.crimsonBright}, 0 0 22px rgba(107,0,21,0.5)` }}>
              ✦ TELEGRAM BOT DELTA
            </motion.a>
            <motion.a href="https://wa.me/deltaprotecintelmx" target="_blank" rel="noopener noreferrer"
              whileHover={{ scale:1.04, boxShadow:"0 0 38px rgba(255,160,40,0.55), 0 0 75px rgba(200,120,20,0.35)" }} whileTap={{ scale:0.97 }}
              style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"11px 24px",
                border:"1px solid rgba(255,160,40,0.55)", background:"rgba(14,10,2,0.6)",
                color:MP.goldBright, fontFamily:DT.terminal, fontSize:"12px", letterSpacing:"0.2em",
                textDecoration:"none", borderRadius:2, cursor:"pointer",
                boxShadow:`0 0 18px rgba(200,120,20,0.3), 0 0 40px rgba(140,80,10,0.2)`,
                textShadow:`0 0 10px ${MP.goldBright}, 0 0 22px rgba(200,120,20,0.5)` }}>
              ✦ WHATSAPP DIRECTO
            </motion.a>
          </div>
          <p style={{ fontFamily:DT.terminal, fontSize:"9px", color:"rgba(255,255,255,0.68)", letterSpacing:"0.15em", marginTop:18 }}>
            TODOS LOS CANALES ESTÁN CIFRADOS · CONFIDENCIALIDAD ABSOLUTA · DELTA PROTEC INTEL
          </p>
        </motion.div>
      </div>
    </div>
  );
}
